import type {
  MonthlyEditableRowValues,
  MonthlyRowEntry,
  SnapshotDetailRow,
} from './interfaces/MonthlyRecord'
import { calcTotals, monthEnd, monthStart } from './monthlyRecordsHelpers'

export type MonthlySnapshotPayload = Omit<
  SnapshotDetailRow,
  'id' | 'snapshot_id' | 'created_at'
>
export type MonthlySavePlan = {
  month: string
  baseSnapshotId: string | null
  snapshotRows: MonthlySnapshotPayload[]
  utilityEdits: { room_number: string; utility_id: string | null }[]
}

function snapshotPayload(
  entry: MonthlyRowEntry,
  form: MonthlyEditableRowValues,
): MonthlySnapshotPayload {
  const totals = calcTotals(form)
  return {
    room_number: entry.room.room_number,
    tenant_name: entry.tenantName ?? null,
    contract_id:
      entry.source === 'snapshot'
        ? (entry.snapshotRow?.contract_id ?? null)
        : entry.existingRecord
          ? entry.existingRecord.util.contract_id
          : (entry.contract?.id ?? null),
    price_monthly: Number(form.price_monthly) || 0,
    elec_unit_price: Number(form.elec_unit_price) || 0,
    elec_prev_reading: Number(form.elec_prev_reading) || 0,
    elec_curr_reading: Number(form.elec_curr_reading) || 0,
    water_unit_price: Number(form.water_unit_price) || 0,
    water_prev_reading: Number(form.water_prev_reading) || 0,
    water_curr_reading: Number(form.water_curr_reading) || 0,
    ...totals,
    internet_fee: Number(form.internet_fee) || 0,
    furniture_fee: Number(form.furniture_fee) || 0,
    maintenance_fee: Number(form.maintenance_fee) || 0,
    other_fee: Number(form.other_fee) || 0,
    other_fee_label: form.other_fee_label,
    payment_status: form.payment_status,
    record_date: form.record_date,
    remark: form.remark || null,
    room_type: form.room_type,
    room_status: form.room_status,
    room_remark: form.room_remark || null,
    repair_details: form.repair_details || null,
    keycards: form.keycards
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
  }
}

export function buildMonthlySavePlan(
  month: string,
  baseSnapshotId: string | null,
  allRows: MonthlyRowEntry[],
  confirmedRows: MonthlyRowEntry[],
): MonthlySavePlan {
  const edits = confirmedRows.filter((row) => row.dirty)
  const editsByRoom = new Map(edits.map((row) => [row.room.room_number, row]))
  for (const edit of edits) {
    if (
      edit.form.record_date < monthStart(month) ||
      edit.form.record_date > monthEnd(month)
    ) {
      throw new Error('Record dates must belong to the selected month')
    }
  }
  const snapshotRows = allRows.map((row) => {
    const edit = editsByRoom.get(row.room.room_number)
    if (edit) return snapshotPayload(edit, edit.form)
    if (row.snapshotRow) {
      const { id, snapshot_id, created_at, deleted_at, ...payload } =
        row.snapshotRow as SnapshotDetailRow & { deleted_at?: string | null }
      return payload
    }
    return snapshotPayload(row, row.initialForm ?? row.snapshotForm ?? row.form)
  })
  return {
    month,
    baseSnapshotId,
    snapshotRows,
    utilityEdits: edits.map((row) => ({
      room_number: row.room.room_number,
      utility_id: row.existingRecord?.util.id ?? null,
    })),
  }
}

export function reconcileSavedRows(
  currentRows: MonthlyRowEntry[],
  savedRows: MonthlyRowEntry[],
  confirmedRooms: string[],
): MonthlyRowEntry[] {
  const confirmed = new Set(confirmedRooms)
  const currentByRoom = new Map(
    currentRows.map((row) => [row.room.room_number, row]),
  )
  return savedRows.map((row) => {
    const current = currentByRoom.get(row.room.room_number)
    return current?.dirty && !confirmed.has(row.room.room_number)
      ? { ...row, form: current.form, errors: current.errors, dirty: true }
      : row
  })
}
